import * as React from 'react'
import { Box, Tab, Tabs, Typography, useTheme, useMediaQuery } from '@mui/material'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
  LocationCity,
  Map,
  Link as LinkIcon,
  Place,
  Public,
  AccountTree,
} from '@mui/icons-material'
import TenantTable from '../../components/Table/tenant'
import CantonTable from '../../components/Table/canton'
import CantonTenantTable from '../../components/Table/cantonTenant'
import ParroquiaTable from '../../components/Table/parroquia'
import CountryTable from '../../components/Table/country'
import ProvinceTable from '../../components/Table/province'
import TreeReassignment from './TreeReassignment'
import * as RT from '../../config/routeType'
import {
  TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE,
  TERRITORIAL_MANAGEMENT_ROUTE,
} from '../../config/routes'

const TERRITORIAL_TABS = [
  { slug: RT.TERRITORIAL_TAB_COUNTRY, label: 'País' },
  { slug: RT.TERRITORIAL_TAB_PROVINCE, label: 'Provincia' },
  { slug: RT.TERRITORIAL_TAB_CANTON, label: 'Cantón' },
  { slug: RT.TERRITORIAL_TAB_PARISH, label: 'Parroquia' },
  { slug: RT.TERRITORIAL_TAB_TENANTS, label: 'Tenants' },
  { slug: RT.TERRITORIAL_TAB_ASSOCIATIONS, label: 'Asociaciones' },
  { slug: RT.TERRITORIAL_TAB_REASSIGNMENT, label: 'Reasignación' },
]

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`territorial-tabpanel-${index}`}
      aria-labelledby={`territorial-tab-${index}`}
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
      }}
      {...other}>
      {value === index && children}
    </Box>
  )
}

function a11yProps(index) {
  return {
    id: `territorial-tab-${index}`,
    'aria-controls': `territorial-tabpanel-${index}`,
  }
}

const TerritorialManagement = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { tab } = useParams()
  const value = TERRITORIAL_TABS.findIndex(item => item.slug === tab)
  const heightBase = useSelector(state => state.drawerLeft.drawerHeight)

  const handleChange = (event, newValue) => {
    const selectedTab = TERRITORIAL_TABS[newValue]
    if (selectedTab) {
      navigate(`${TERRITORIAL_MANAGEMENT_ROUTE}/${selectedTab.slug}`, { replace: false })
    }
  }

  if (value === -1) {
    return <Navigate to={TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE} replace />
  }

  return (
    <Box
      sx={{
        width: '100%',
        mt: heightBase,
        height: `calc(100vh - ${heightBase})`,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}>
        <Typography
          variant="h6"
          sx={{
            p: 2,
            pb: 1,
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}>
          Administración Territorial
        </Typography>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="territorial management tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
            },
          }}>
          <Tab
            icon={<Public />}
            iconPosition="start"
            label={TERRITORIAL_TABS[0].label}
            {...a11yProps(0)}
          />
          <Tab
            icon={<AccountTree />}
            iconPosition="start"
            label={
              <Box textAlign="left">
                <Typography fontWeight={600}>{TERRITORIAL_TABS[1].label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Departamentos / Región
                </Typography>
              </Box>
            }
            {...a11yProps(1)}
          />
          <Tab
            icon={<Map />}
            iconPosition="start"
            label={
              <Box textAlign="center">
                <Typography fontWeight={600}>{TERRITORIAL_TABS[2].label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Municipio / Provincia
                </Typography>
              </Box>
            }
            {...a11yProps(2)}
          />
          <Tab
            icon={<Place />}
            iconPosition="start"
            label={
              <Box textAlign="center">
                <Typography fontWeight={600}>{TERRITORIAL_TABS[3].label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Distrito / Comuna
                </Typography>
              </Box>
            }
            {...a11yProps(3)}
          />
          <Tab
            icon={<LocationCity />}
            iconPosition="start"
            label={
              <Box textAlign="center">
                <Typography fontWeight={600}>{TERRITORIAL_TABS[4].label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Instituciones / Organizaciones
                </Typography>
              </Box>
            }
            {...a11yProps(4)}
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label={
              <Box textAlign="center">
                <Typography fontWeight={600}>{TERRITORIAL_TABS[5].label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`Cantón & Tenants`}
                </Typography>
              </Box>
            }
            {...a11yProps(5)}
          />
          <Tab
            icon={<LinkIcon />} // You might want to choose a different icon
            iconPosition="start"
            label={TERRITORIAL_TABS[6].label}
            {...a11yProps(6)}
          />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <CountryTable />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <ProvinceTable />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <CantonTable />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <ParroquiaTable />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <TenantTable />
      </TabPanel>
      <TabPanel value={value} index={5}>
        <CantonTenantTable />
      </TabPanel>
      <TabPanel value={value} index={6}>
        <TreeReassignment />
      </TabPanel>
    </Box>
  )
}

export default TerritorialManagement
